import { isEqual } from 'lodash'
import { createWithEqualityFn } from 'zustand/traditional'

export type DrawerRender = (onClose: () => void) => React.ReactElement
export type PanelId = 'chatbot' | 'logTime'

const LEGACY_PANEL_ID: PanelId = 'chatbot'

interface PanelEntry {
  id: PanelId
  render: DrawerRender
}

interface AppDrawerState {
  panels: PanelEntry[]
  closing: PanelEntry[]
  isOccupied: boolean
  open: (idOrRender: PanelId | DrawerRender, render?: DrawerRender) => void
  close: (id?: PanelId) => void
  animationDidEnd: (id: PanelId) => void
}

const resolveOpenArgs = (
  idOrRender: PanelId | DrawerRender,
  render?: DrawerRender,
): PanelEntry =>
  typeof idOrRender === 'function'
    ? { id: LEGACY_PANEL_ID, render: idOrRender }
    : { id: idOrRender, render: render as DrawerRender }

export const useAppDrawerStore = createWithEqualityFn<AppDrawerState>()(
  (set, get) => ({
    panels: [],
    closing: [],
    isOccupied: false,

    open: (idOrRender, render) => {
      const { id, render: panelRender } = resolveOpenArgs(idOrRender, render)
      const { panels, closing } = get()
      const exists = panels.some((p) => p.id === id)
      const nextPanels = exists
        ? panels.map((p) => (p.id === id ? { id, render: panelRender } : p))
        : [...panels, { id, render: panelRender }]
      set({
        panels: nextPanels,
        closing: closing.filter((p) => p.id !== id),
        isOccupied: nextPanels.some((p) => p.id === LEGACY_PANEL_ID),
      })
    },

    close: (id = LEGACY_PANEL_ID) => {
      const { panels, closing } = get()
      const entry = panels.find((p) => p.id === id)
      if (!entry) return
      const nextPanels = panels.filter((p) => p.id !== id)
      set({
        panels: nextPanels,
        closing: closing.some((p) => p.id === id)
          ? closing
          : [...closing, entry],
        isOccupied: nextPanels.some((p) => p.id === LEGACY_PANEL_ID),
      })
    },

    animationDidEnd: (id) => {
      set({ closing: get().closing.filter((p) => p.id !== id) })
    },
  }),
  isEqual,
)
